import { format, parseISO } from 'date-fns';

/**
 * Formats a date string or Date object to ISO date string (yyyy-MM-dd)
 * Used for form inputs and API requests
 */
export const formatDateISO = (date: string | Date | undefined): string => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Formats a date string or Date object to a display-friendly format
 * Example: "Jan 15, 2024"
 */
export const formatDateDisplay = (date: string | Date | undefined): string => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM d, yyyy');
  } catch {
    return typeof date === 'string' ? date : '-';
  }
};

/**
 * Formats a date string or Date object to a short display format
 * Example: "Jan 15"
 */
export const formatDateShort = (date: string | Date | undefined): string => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM d');
  } catch {
    return typeof date === 'string' ? date : '-';
  }
};

/**
 * Parses a date string (ISO format or with time) to a Date object
 * Handles date strings that may or may not include time
 */
export const parseDate = (dateString: string | undefined): Date | undefined => {
  if (!dateString) return undefined;
  try {
    const dateStr = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
    const date = parseISO(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
  } catch {
    return undefined;
  }
};

/**
 * Legacy function for backward compatibility
 * Formats a date string or Date object to ISO date string (yyyy-MM-dd)
 * @deprecated Use formatDateISO instead
 */
export const formatDate = formatDateISO;
